import React, { useLayoutEffect, useRef, useState } from 'react'
import Marquee from 'react-fast-marquee'

import { type HomepageLogo } from '../../types'
import { canHoverWithPointer } from '../../utils/pointer'
import styles from './styles.module.scss'

type FeaturedOnProps = {
  title: string
  logos: HomepageLogo[]
  marqueeEnabled?: boolean
}

const FeaturedLogo = ({ logo }: { logo: HomepageLogo }) => {
  const className = `${styles.logoItem}${logo.format === 'square' ? ` ${styles.squareLogo}` : ''}`
  const image = <img src={logo.image} alt={`${logo.name} logo`} />

  return logo.url ? (
    <a
      className={className}
      href={logo.url}
      target="_blank"
      rel="noreferrer"
      aria-label={logo.name}
    >
      {image}
    </a>
  ) : (
    <div className={className}>{image}</div>
  )
}

const FeaturedOn = ({ title, logos, marqueeEnabled = true }: FeaturedOnProps) => {
  const viewportRef = useRef<HTMLDivElement>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const [pauseOnPointer] = useState(canHoverWithPointer)

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const row = rowRef.current
    if (!viewport || !row) return

    const measureOverflow = () => setHasOverflow(row.scrollWidth > viewport.clientWidth)
    const observer = new ResizeObserver(measureOverflow)
    observer.observe(viewport)
    observer.observe(row)
    measureOverflow()
    return () => observer.disconnect()
  }, [logos])

  const isAnimating = marqueeEnabled && hasOverflow
  const logoItems = logos.map((logo) => <FeaturedLogo key={logo.name} logo={logo} />)

  return (
    <section className={styles.section} aria-labelledby="featured-on-title">
      <div className={styles.content}>
        <div className={styles.titleRow}>
          <h2 id="featured-on-title">{title}</h2>
          <span aria-hidden />
        </div>
        <div
          className={`${styles.marqueeViewport}${isAnimating ? '' : ` ${styles.staticViewport}`}`}
          aria-label="Featured publications"
          data-marquee-active={isAnimating}
          ref={viewportRef}
        >
          <div
            className={`${styles.logoRow}${isAnimating ? ` ${styles.measuringRow}` : ''}`}
            aria-hidden={isAnimating || undefined}
            ref={rowRef}
          >
            {logoItems}
          </div>
          {isAnimating ? (
            <Marquee
              className={styles.marquee}
              autoFill
              pauseOnHover={pauseOnPointer}
              pauseOnClick={pauseOnPointer}
              speed={38}
              gradient={false}
            >
              {logoItems}
            </Marquee>
          ) : null}
        </div>
      </div>
    </section>
  )
}

export default FeaturedOn
